/**
 * Habit domain logic — pure functions, no React and no storage.
 *
 * DATA MODEL
 * ----------
 * Habit {
 *   id:         string
 *   name:       string
 *   repeat:     'once' | 'daily' | 'weekly'
 *   startDate:  'YYYY-MM-DD'          first (anchor) occurrence
 *   endDate:    'YYYY-MM-DD' | null   exclusive stop for weekly repeats
 *   exceptions: { [baseDateKey]: string | null }
 *                                     string = this occurrence was moved to that day
 *                                     null   = this occurrence was deleted
 *   createdAt:  number                used only for stable ordering
 * }
 *
 * Completions are a flat map keyed by `habitId|baseDateKey`. Keying on the
 * *base* date (not the moved-to date) means a tick survives a drag.
 */

import { addDays, daysBetween, parseDateKey, toDateKey, weekdayIndex } from './date.js';
import { HABIT_KIND, SPORT_KIND } from './sport.js';

export const REPEAT_ONCE = 'once';
export const REPEAT_DAILY = 'daily';
export const REPEAT_WEEKLY = 'weekly';

/** Every value `repeat` may hold. */
export const REPEAT_VALUES = [REPEAT_ONCE, REPEAT_DAILY, REPEAT_WEEKLY];

/**
 * The repeat choices as the UI offers them, ordered by how much they ask of
 * you: a one-off first, then weekly, then every day. Shared by the habit line
 * and the sport modal so the three never appear in two different orders.
 */
export const REPEAT_CHOICES = [
  { value: REPEAT_ONCE, label: 'once' },
  { value: REPEAT_WEEKLY, label: 'weekly' },
  { value: REPEAT_DAILY, label: 'daily' },
];

/**
 * Coerce any input into a valid repeat value.
 * @param {unknown} value
 * @returns {'once'|'daily'|'weekly'}
 */
export function normalizeRepeat(value) {
  return REPEAT_VALUES.includes(value) ? value : REPEAT_ONCE;
}

/**
 * Does this habit produce more than one occurrence?
 * Used wherever "one day or the whole series?" needs asking.
 * @param {object} habit
 * @returns {boolean}
 */
export function isRepeating(habit) {
  return habit?.repeat === REPEAT_DAILY || habit?.repeat === REPEAT_WEEKLY;
}

/** Days between occurrences of a repeating habit. */
function repeatStep(habit) {
  return habit.repeat === REPEAT_DAILY ? 1 : 7;
}

/** Generate a reasonably unique id without pulling in a dependency. */
export function makeId() {
  return `h_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Build a new habit object.
 *
 * `sport` is the optional blueprint from the sport modal. Sport habits are
 * ordinary habits in every other respect — same schedule, drag and delete
 * rules — so nothing else in this module needs to know about them.
 *
 * @param {{name: string, repeat?: string, dateKey: string, sport?: object|null, now?: number}} input
 * @returns {object} habit
 */
export function createHabit({
  name,
  repeat = REPEAT_ONCE,
  dateKey,
  sport = null,
  now = Date.now(),
}) {
  return {
    id: makeId(),
    name: String(name).trim(),
    kind: sport ? SPORT_KIND : HABIT_KIND,
    sport: sport || null,
    repeat: normalizeRepeat(repeat),
    startDate: dateKey,
    endDate: null,
    exceptions: {},
    createdAt: now,
  };
}

/**
 * Does `dateKey` sit on the habit's natural (pre-exception) schedule?
 * @param {object} habit
 * @param {string} dateKey
 * @returns {boolean}
 */
export function isBaseOccurrence(habit, dateKey) {
  if (dateKey < habit.startDate) return false;
  if (habit.repeat === REPEAT_ONCE) return dateKey === habit.startDate;
  if (habit.endDate && dateKey >= habit.endDate) return false;
  // Daily: every day from the anchor onwards.
  if (habit.repeat === REPEAT_DAILY) return true;
  // Weekly: same weekday as the anchor.
  return weekdayIndex(parseDateKey(dateKey)) === weekdayIndex(parseDateKey(habit.startDate));
}

/**
 * Resolve a base occurrence through the exception map.
 * @param {object} habit
 * @param {string} baseDateKey
 * @returns {string|null} the effective date key, or null when deleted
 */
export function resolveOccurrence(habit, baseDateKey) {
  const ex = habit.exceptions || {};
  if (Object.prototype.hasOwnProperty.call(ex, baseDateKey)) return ex[baseDateKey];
  return baseDateKey;
}

/**
 * Every base occurrence of a habit that falls inside [startKey, endKey].
 * @param {object} habit
 * @param {string} startKey
 * @param {string} endKey
 * @returns {string[]}
 */
export function baseOccurrencesInRange(habit, startKey, endKey) {
  if (habit.repeat === REPEAT_ONCE) {
    return habit.startDate >= startKey && habit.startDate <= endKey ? [habit.startDate] : [];
  }

  const out = [];
  const step = repeatStep(habit); // 1 for daily, 7 for weekly
  const anchor = parseDateKey(habit.startDate);

  // Jump straight to the first occurrence at or after startKey rather than
  // stepping there — a daily habit anchored years ago would be a long walk.
  const behind = daysBetween(anchor, parseDateKey(startKey));
  const skip = behind > 0 ? Math.ceil(behind / step) : 0;

  let cursor = addDays(anchor, skip * step);
  let key = toDateKey(cursor);

  while (key <= endKey) {
    if (habit.endDate && key >= habit.endDate) break;
    out.push(key);
    cursor = addDays(cursor, step);
    key = toDateKey(cursor);
  }
  return out;
}

/**
 * All visible habit occurrences within a date range, exceptions applied.
 * @param {object[]} habits
 * @param {string} startKey
 * @param {string} endKey
 * @returns {{habit: object, date: string, baseDate: string}[]}
 */
export function getOccurrencesInRange(habits, startKey, endKey) {
  const out = [];

  for (const habit of habits) {
    const handled = new Set();

    for (const base of baseOccurrencesInRange(habit, startKey, endKey)) {
      handled.add(base);
      const effective = resolveOccurrence(habit, base);
      if (effective === null) continue; // deleted for this day
      if (effective >= startKey && effective <= endKey) {
        out.push({ habit, date: effective, baseDate: base });
      }
    }

    // Occurrences dragged *into* this range from a base date outside it.
    for (const [base, target] of Object.entries(habit.exceptions || {})) {
      if (handled.has(base) || target == null) continue;
      if (target >= startKey && target <= endKey && isBaseOccurrence(habit, base)) {
        out.push({ habit, date: target, baseDate: base });
      }
    }
  }

  return out.sort((a, b) => a.habit.createdAt - b.habit.createdAt);
}

/**
 * Group occurrences by their effective date key.
 * @param {{date: string}[]} occurrences
 * @returns {Record<string, object[]>}
 */
export function groupByDate(occurrences) {
  const map = {};
  for (const occ of occurrences) {
    (map[occ.date] ||= []).push(occ);
  }
  return map;
}

/** Completion map key for a single occurrence. */
export function completionKey(habitId, baseDateKey) {
  return `${habitId}|${baseDateKey}`;
}

/**
 * @param {Record<string, boolean>} completions
 * @param {string} habitId
 * @param {string} baseDateKey
 * @returns {boolean}
 */
export function isCompleted(completions, habitId, baseDateKey) {
  return Boolean(completions[completionKey(habitId, baseDateKey)]);
}

/**
 * Flip the done state of a single occurrence. Returns a new map.
 * @param {Record<string, boolean>} completions
 * @param {string} habitId
 * @param {string} baseDateKey
 * @returns {Record<string, boolean>}
 */
export function toggleCompletion(completions, habitId, baseDateKey) {
  const key = completionKey(habitId, baseDateKey);
  const next = { ...completions };
  if (next[key]) delete next[key];
  else next[key] = true;
  return next;
}

/**
 * Move one occurrence to another day. Returns a new habit object.
 * @param {object} habit
 * @param {string} baseDateKey occurrence being dragged
 * @param {string} targetDateKey day it was dropped on
 * @returns {object}
 */
export function moveOccurrence(habit, baseDateKey, targetDateKey) {
  const exceptions = { ...(habit.exceptions || {}) };

  // A one-off habit has no schedule to preserve — just re-anchor it.
  if (habit.repeat === REPEAT_ONCE) {
    delete exceptions[baseDateKey];
    return { ...habit, startDate: targetDateKey, exceptions };
  }

  if (targetDateKey === baseDateKey) delete exceptions[baseDateKey];
  else exceptions[baseDateKey] = targetDateKey;

  return { ...habit, exceptions };
}

/**
 * Remove a single occurrence of a repeating habit. Returns a new habit object.
 * @param {object} habit
 * @param {string} baseDateKey
 * @returns {object}
 */
export function deleteOccurrence(habit, baseDateKey) {
  return {
    ...habit,
    exceptions: { ...(habit.exceptions || {}), [baseDateKey]: null },
  };
}

/**
 * Stop a repeating habit from `baseDateKey` onward (keeps past history intact).
 * @param {object} habit
 * @param {string} baseDateKey
 * @returns {object}
 */
export function endSeriesFrom(habit, baseDateKey) {
  // Drop exceptions that now sit past the end so the object stays tidy.
  const exceptions = Object.fromEntries(
    Object.entries(habit.exceptions || {}).filter(([key]) => key < baseDateKey)
  );
  return { ...habit, endDate: baseDateKey, exceptions };
}

/**
 * True when a repeating habit no longer produces any occurrence at all.
 * @param {object} habit
 * @returns {boolean}
 */
export function isEmptySeries(habit) {
  return isRepeating(habit) && habit.endDate != null && habit.endDate <= habit.startDate;
}

/**
 * Completion ratio for a set of occurrences — used for the month progress note.
 * @param {{habit: object, baseDate: string}[]} occurrences
 * @param {Record<string, boolean>} completions
 * @returns {{done: number, total: number}}
 */
export function countProgress(occurrences, completions) {
  const done = occurrences.filter((o) => isCompleted(completions, o.habit.id, o.baseDate)).length;
  return { done, total: occurrences.length };
}

/**
 * Has this day been cleared? True only when there was something to do and all
 * of it is struck through — an empty day is not an achievement.
 * @param {{habit: object, baseDate: string}[]} occurrences one day's worth
 * @param {Record<string, boolean>} completions
 * @returns {boolean}
 */
export function isDayComplete(occurrences, completions) {
  const { done, total } = countProgress(occurrences, completions);
  return total > 0 && done === total;
}
